import { Form, Formik } from 'formik';
import { useCallback } from 'react';
import * as Yup from 'yup';

import { FormikField } from '@air/formik-field';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';

const RecentlyDeletedLibraryConflictModalFormSchema = Yup.object()
  .shape({
    originalTitle: Yup.string().trim().required(),
    title: Yup.string()
      .trim()
      .required('Cannot be empty')
      .default('')
      .notOneOf([Yup.ref('originalTitle')], 'Cannot be the same as the original title'),
  })
  .required();

export type RecentlyDeletedRecoverLibraryConflictModalProps = {
  title: string;
  onSubmit: (newTitle: string) => void;
};

export const RecentlyDeletedRecoverLibraryConflictModal = ({
  onSubmit,
  onClose,
  title,
}: AirModalProps<RecentlyDeletedRecoverLibraryConflictModalProps>) => {
  const handleSubmit = useCallback(
    ({ title }: { title: string }) => {
      onSubmit(title);
      onClose();
    },
    [onClose, onSubmit],
  );

  return (
    <Modal isOpen onDismiss={onClose}>
      <Formik
        initialValues={{ title: '', originalTitle: title }}
        onSubmit={handleSubmit}
        validationSchema={RecentlyDeletedLibraryConflictModalFormSchema}
      >
        <>
          <div className="mb-5 flex justify-between">
            <ModalTitle>Rename library</ModalTitle>
            <ModalCloseButton className="shrink-0" onClick={onClose} />
          </div>
          <ModalDescription>
            <p className="mb-3">The name &lsquo;{title}&rsquo; is currently in use.</p>
            <p className="mb-3">To recover this library, you will have to rename it.</p>
          </ModalDescription>

          <Form>
            <FormikField
              id="title"
              className="flex-1"
              component={<Input data-1p-ignore placeholder={`${title} (Recovered)`} size="large" />}
              label=""
              name="title"
            />

            <div className="-mx-6 -mb-6 flex justify-end gap-3 px-6 py-4">
              <Button appearance="ghost" color="grey" onClick={onClose} size="large">
                Cancel
              </Button>
              <Button type="submit">Rename</Button>
            </div>
          </Form>
        </>
      </Formik>
    </Modal>
  );
};
